import React, {useEffect, useRef} from 'react'
import {useIntlEx} from "./IntlUtils.js"
import {TagLabel} from './TagBox'
import getLogger from "./Logging.js"
import {useSolv} from "./SolvProvider";
import {BsCol, BsRow, FormGroup, FormGroups, Icon} from "./FormComps";
import {PercentageBar, PieChart3} from "./GraphicComp";
import CreativePreview from "../CreativePreview";
import styles from "./Widget.module.css";
import {Widget, WidgetMetric} from "./Widget";
import {ceilNum, getPropertyComputedValue, truncNum} from "./Utils";

const log = getLogger("BroadcastUtils")

export function BroadcastStatusTagLabel(props) {
  const { intl } = useIntlEx()
  const user = props.user
  const broadcast = props.broadcast
  const size = (props.size ? props.size : "md")
  // const size = (props.size ? ` tag-label-${props.size}` : "")
  let realStatus = (broadcast ? broadcast.status : props.status)?.toLowerCase()
  if (realStatus) {
    const displayStatus = getBroadcastDisplayStatus(realStatus, user)
    let title = intl.msg(`status_broadcast_${displayStatus}`)
    let label = intl.msg(`status_broadcast_${displayStatus}`)
    let pct = getPctDelivered(broadcast, intl)
    return (
      displayStatus &&
      <TagLabel
        label={label}
        title={title}
        size={size}
        className={"broadcast-status-color-" + displayStatus + (props.className ? ` ${props.className}` : "")}>
        {
          pct ? (
            <PercentageBar percentage={Math.min(pct, 99.00)} width="40px"/>
          ) : (
            <></>
          )
        }
      </TagLabel>
    )
  }
  else {
    return <></>
  }
}

export function BroadcastDeliveryPercentage(props) {
  const {intl} = useIntlEx()
  const user = props.user
  const broadcast = props.broadcast
  const size = (props.size ? props.size : "md")
  let realStatus = (broadcast ? broadcast.status : props.status)?.toLowerCase()
  if (realStatus) {
    let pct = getPctDelivered(broadcast, intl)
    return (
      pct ? (
        <PercentageBar percentage={Math.min(pct, 99.00)} size={size}/>
      ) : (
        <></>
      )
    )
  }
  else {
    return <></>
  }
}

export function getPctDelivered(broadcast) {
  const adViews = broadcast?.adViews || broadcast?.stats?.adViews
  // log.debug(">>>Z: getPctDelivered: broadcast=", broadcast)
  if (broadcast && broadcast.status && adViews && broadcast.targetAdViews && ("STARTED" === broadcast.status)) {
    return adViews < broadcast.targetAdViews ? Math.min((adViews + 0.0) / (broadcast.targetAdViews + 0.0) * 100, 99.0) : 99.0
  }
  else {
    return null
  }
}

export function getStyle(style, el, at, defaultValue) {
  if (style && style[el] && style[el][at]) {
    return style[el][at]
  }
  return defaultValue
}

export function mkFieldRowClassName(opts) {
  let className = "field-row"
  if (opts) {
    if (opts.layout) {
      className = ` ${className} field-style-${opts.layout.toLowerCase()}`
    }
    if (opts.labelStyle) {
      className = ` ${className} field-style-label-${opts.labelStyle.toLowerCase()}`
    }
    if (opts.valueStyle) {
      className = `${className} field-style-value-${opts.valueStyle.toLowerCase()}`
    }
  }
  return className
}

export function getMessageFormatOpts(tenant, intl) {
  let opts = []
  opts.push({
    label: <><div>{intl.msg("broadcast_message_type_solv")}</div><small>{intl.msg("broadcast_message_type_solv_tooltip")}</small></>,
    value: "SOLV",
  })
  opts.push({
    label: <><div>{intl.msg("broadcast_message_type_image")}</div><small>{intl.msg("broadcast_message_type_image_tooltip")}</small></>,
    value: "IMAGE",
  })
  return opts
}

export function getMessageFormatOpt(messageFormat, tenant, intl) {
  return getMessageFormatOpts(tenant, intl).find(i => i.value === messageFormat)
}

export function useClickActionOpts(filter) {
  const {intl} = useIntlEx()
  let clickActionOpts = [
    {
      value: "EXTERNAL",
      text: intl.msg("broadcast_page_type_custom_page"),
      description: intl.msg("broadcast_page_type_custom_page_tooltip"),
      icon: <Icon name="clickaction_www"/>,
      pageUrl: (destination) => destination,
      destination: (pageUrl) => pageUrl,
    },
    {
      value: "EMAIL",
      text: intl.msg("broadcast_page_type_email"),
      description: intl.msg("broadcast_page_type_email_description"),
      icon: <Icon name="clickaction_email"/>,
      pageUrl: (destination, params) => `mailto:${encodeURIComponent(destination?.trim())}${params ? "?" + Object.entries(params).map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v || "")}`).join(',') : ""}`,
      destination: (pageUrl) => pageUrl?.trim()?.startsWith("mailto:") ? decodeURIComponent(pageUrl.trim().slice("mailto:".length).split('?')[0]) : pageUrl?.trim()
    },
    {
      value: "PHONE",
      text: intl.msg("broadcast_page_type_phone"),
      description: intl.msg("broadcast_page_type_phone_description"),
      icon: <Icon name="clickaction_phone"/>,
      pageUrl: (destination) => `tel:${encodeURIComponent(destination?.trim()?.replace(/(?!^\+)[^0-9]/g, ""))}`,
      destination: (pageUrl, countryCode) => pageUrl?.trim()?.startsWith("tel:") ? decodeURIComponent(pageUrl.trim()?.slice("tel:".length)) : pageUrl?.trim(),
    },
    {
      value: "HOME",
      text: intl.msg("broadcast_page_type_home_page"),
      description: intl.msg("broadcast_page_type_home_page_description"),
      icon: <Icon name="clickaction_www"/>,
      pageUrl: (destination) => destination,
      destination: (pageUrl) => pageUrl,
    },
    {
      value: "__SEP1",
      isDisabled: true,
    },
    {
      value: "SMS",
      text: intl.msg("broadcast_page_type_sms"),
      description: intl.msg("broadcast_page_type_sms_description"),
      icon: <Icon name="clickaction_sms"/>,
      pageUrl: (destination) => `sms:${encodeURIComponent(destination?.trim()?.replace(/(?!^\+)[^0-9]/g, ""))}`,
      destination: (pageUrl, countryCode) => pageUrl?.trim()?.startsWith("sms:") ? decodeURIComponent(pageUrl.trim()?.slice("sms:".length)) : pageUrl?.trim(),
    },
    {
      value: "FBMESSENGER",
      text: intl.msg("broadcast_page_type_fbmessenger"),
      description: intl.msg("broadcast_page_type_fbmessenger_description"),
      icon: <Icon name="clickaction_facebook"/>,
      pageUrl: (destination) => `http://m.me/${encodeURIComponent(destination?.trim())}`,
      destination: (pageUrl) => pageUrl?.trim()?.startsWith("http://m.me/") ? decodeURIComponent(pageUrl.trim()?.slice("http://m.me/".length)) : pageUrl?.trim(),
    },
    {
      value: "LINE",
      text: intl.msg("broadcast_page_type_line"),
      description: intl.msg("broadcast_page_type_line_description"),
      icon: <Icon name="clickaction_line"/>,
      pageUrl: (destination) => `https://line.me/R/oaMessage/${encodeURIComponent(destination?.trim())}`,
      destination: (pageUrl) => pageUrl?.trim()?.startsWith("https://line.me/R/oaMessage/") ? decodeURIComponent(pageUrl.trim()?.slice("https://line.me/R/oaMessage/".length)) : pageUrl?.trim(),
    },
    {
      value: "SIGNAL",
      text: intl.msg("broadcast_page_type_signal"),
      description: intl.msg("broadcast_page_type_signal_description"),
      icon: <Icon name="clickaction_signal"/>,
      pageUrl: (destination) => `https://signal.me/#p/destination?.trim()}`,
      destination: (pageUrl) => pageUrl?.trim()?.startsWith("https://signal.me/#p/") ? decodeURIComponent(pageUrl.trim()?.slice("https://signal.me/#p/".length)) : pageUrl?.trim(),
    },
    {
      value: "TELEGRAM",
      text: intl.msg("broadcast_page_type_telegram"),
      description: intl.msg("broadcast_page_type_telegram_description"),
      icon: <Icon name="clickaction_telegram"/>,
      pageUrl: (destination) => `http://t.me/${encodeURIComponent(destination?.trim())}`,
      destination: (pageUrl) => pageUrl?.trim()?.startsWith("http://t.me/") ? decodeURIComponent(pageUrl.trim()?.slice("http://t.me/".length)) : pageUrl?.trim(),
    },
    {
      value: "VIBER",
      text: intl.msg("broadcast_page_type_viber"),
      description: intl.msg("broadcast_page_type_viber_description"),
      icon: <Icon name="clickaction_viber"/>,
      pageUrl: (destination) => `viber://${encodeURIComponent(destination?.trim()?.replace(/(?!^\+)[^0-9]/g, ""))}`,
      destination: (pageUrl, countryCode) => pageUrl?.trim()?.startsWith("viber://") ? decodeURIComponent(pageUrl.trim()?.slice("viber://".length)) : pageUrl?.trim(),
    },
    {
      value: "WHATSAPP",
      text: intl.msg("broadcast_page_type_whatsapp"),
      description: intl.msg("broadcast_page_type_whatsapp_description"),
      icon: <Icon name="clickaction_whatsapp"/>,
      pageUrl: (destination) => `https://wa.me/${encodeURIComponent(destination?.trim()?.replace(/(?!^\+)[^0-9]/g, ""))}`,
      destination: (pageUrl) => pageUrl?.trim()?.startsWith("https://wa.me/") ? decodeURIComponent(pageUrl.trim()?.slice("https://wa.me/".length)) : pageUrl?.trim(),
    },
  ]

  clickActionOpts.forEach(o => {
    if (!o.label) {
      if (!o.isDisabled) {
        o.data = {
          type: o.value,
        }
        o.label = <>
          <div className="d-flex align-items-center gap-1">
            {o.icon}
            <div>{o.text}</div>
          </div>
          <small>{o.description}</small>
        </>
      }
      else {
        o.label = <>
          <div style={{borderTop: "1px solid var(--form-control-react-select-option-disabled-text-color)", height: "8px"}}>
          </div>
        </>
      }
    }
  })

  // clickActionOpts.sort((a, b) => b.text.localeCompare(a.text))

  if (Array.isArray(filter)) {
    clickActionOpts = clickActionOpts.filter((c) => c.value.startsWith("__SEP") || filter.includes(c.value))
  }
  else if ("function" === typeof(filter)) {
    clickActionOpts = clickActionOpts.map(filter).filter(Boolean)
  }

  return ({
    clickActionOpts: [...clickActionOpts],
    findClickActionOpt: (v, def) => {
      return clickActionOpts.find(e => e.value?.toUpperCase() === v)
        || clickActionOpts.find(e => e.value?.toUpperCase() === def)
    },
  })
}

export function useBroadcastStartTimeOpt(filter) {

  function generateAsapOpt() {
    return (
      {
        type: "ASAP",
        value: "ASAP",
        text: intl.msg("broadcast_target_schedule_start_time_asap"),
        label: intl.msg("broadcast_target_schedule_start_time_asap"),
      }
    )
  }

  function generateOpts(days = 7) {

    const options = [generateAsapOpt()]
    options.push(
      {
        label: <>
          <div style={{borderTop: "1px solid var(--form-control-react-select-option-disabled-text-color)", height: "8px"}}>
          </div>
        </>,
        isDisabled: true
      }
    )

    const now = new Date();

    for (let i = 1; i <= days; i++) {
      const date = new Date(now.getFullYear(), now.getMonth(), now.getDate() + i, 0, 0, 0, 0);
      const timestamp = date.getTime();
      const label = date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

      options.push({
        type: "TIMESTAMP",
        value: timestamp,
        text: label,
        label: label,
        timestamp: timestamp,
      });
    }

    return options;
  }

  function generateOpt(timestamp) {
    if (timestamp === null || timestamp === 0) {
      return generateAsapOpt()
    }

    const date = new Date(timestamp);
    const label = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

    return ({
      type: "TIMESTAMP",
      label: label,
      text: label,
      value: timestamp,
      timestamp: timestamp,
    })

  }

  const {intl} = useIntlEx()
  const broadcastStartTimeOpts = generateOpts(30)

  return ({
    broadcastStartTimeOpts: broadcastStartTimeOpts,
    findBroadcastStartTimeOpt: (v) => {

      function getNum(t) {
        switch (t?.type) {
          case "ASAP":
            return 0
          case "HOURS":
            return t?.hours
          case "DAYS":
            return t?.days
          case "WEEKS":
            return t?.weeks
          case "TIMESTAMP":
            return t?.timestamp
          default:
            return 0
        }
      }

      switch (typeof(v)) {
        case "string":
          return broadcastStartTimeOpts.find(e => e.value?.toUpperCase() === v)
            || broadcastStartTimeOpts.find(e => e.value?.toUpperCase() === generateOpt(v))
        case "object":
          if (v !== null) {
            return broadcastStartTimeOpts.find(e => ((e.type?.toUpperCase() === v.type.toUpperCase()) && (getNum(e) === getNum(v)))) || generateOpt(getNum(v))
          }
          else {
            return generateAsapOpt()
          }
        default:
          generateOpt(getNum(v))
      }
    },
  })

}

export function useBroadcastEndTimeOpt(filter) {
  const {intl} = useIntlEx()
  const broadcastEndTimeOpts = [
    {
      value: "ASAP",
      text: intl.msg("broadcast_target_schedule_end_time_asap"),
      label: intl.msg("broadcast_target_schedule_end_time_asap"),
      type: "ASAP",
    },
    {
      value: "2D",
      text: intl.msg("broadcast_target_schedule_end_time_2_days"),
      label: intl.msg("broadcast_target_schedule_end_time_2_days"),
      type: "DAYS",
      days: 2,
    },
    {
      value: "3D",
      text: intl.msg("broadcast_target_schedule_end_time_3_days"),
      label: intl.msg("broadcast_target_schedule_end_time_3_days"),
      type: "DAYS",
      days: 3,
    },
    {
      value: "4D",
      text: intl.msg("broadcast_target_schedule_end_time_4_days"),
      label: intl.msg("broadcast_target_schedule_end_time_4_days"),
      type: "DAYS",
      days: 4,
    },
    {
      value: "5D",
      text: intl.msg("broadcast_target_schedule_end_time_5_days"),
      label: intl.msg("broadcast_target_schedule_end_time_5_days"),
      type: "DAYS",
      days: 5,
    },
    {
      value: "1W",
      text: intl.msg("broadcast_target_schedule_end_time_1_weeks"),
      label: intl.msg("broadcast_target_schedule_end_time_1_weeks"),
      type: "WEEKS",
      weeks: 1,
    },
    {
      value: "2W",
      text: intl.msg("broadcast_target_schedule_end_time_2_weeks"),
      label: intl.msg("broadcast_target_schedule_end_time_2_weeks"),
      type: "WEEKS",
      weeks: 2,
    },
    {
      value: "3W",
      text: intl.msg("broadcast_target_schedule_end_time_3_weeks"),
      label: intl.msg("broadcast_target_schedule_end_time_3_weeks"),
      type: "WEEKS",
      weeks: 3,
    },
    {
      value: "4W",
      text: intl.msg("broadcast_target_schedule_end_time_4_weeks"),
      label: intl.msg("broadcast_target_schedule_end_time_4_weeks"),
      type: "WEEKS",
      weeks: 4,
    }
  ]
  return ({
    broadcastEndTimeOpts: broadcastEndTimeOpts,
    findBroadcastEndTimeOpt: (v, def) => {
      function getNum(t) {
        switch (t?.type) {
          case "HOURS":
            return t?.hours
          case "DAYS":
            return t?.days
          case "WEEKS":
            return t?.weeks
          default:
            return 0
        }
      }
      switch (typeof(v)) {
        case "string":
          return broadcastEndTimeOpts.find(e => e.value?.toUpperCase() === v)
            || broadcastEndTimeOpts.find(e => e.value?.toUpperCase() === def)
        case "object":
          return broadcastEndTimeOpts.find(e => ((e.type?.toUpperCase() === v.type.toUpperCase()) && (getNum(e) === getNum(v))))
            || def
        default:
          return def
      }
    },
  })

}

function getBroadcastDisplayStatus(status, user) {
  let s = status?.trim()?.toLowerCase()
  if (user?.isSystemUser()) {
    return s
  }
  else {
    switch (s) {
      case "submitted":
      case "publishing":
      case "publish_failed":
        return "awaiting_review"
      case "published":
        return "awaiting_stats"
      default:
        return s
    }
  }
}

export function useBroadcastStatusOpts(props) {
  const {session:{user}} = useSolv()
  const {intl} = useIntlEx()
  let broadcastStatusOpts = [
    {
      value: "DRAFT",
      label: intl.msg("status_broadcast_draft"),
    },
  ]
  if (user?.isSystemUser()) {
    broadcastStatusOpts.push(
      {
        value: "SUBMITTED",
        label: intl.msg("status_broadcast_submitted"),
      }
    )
    broadcastStatusOpts.push(
      {
        value: "PUBLISHED",
        label: intl.msg("status_broadcast_published"),
      }
    )
    broadcastStatusOpts.push(
      {
        value: "PUBLISH_FAILED",
        label: intl.msg("status_broadcast_publish_failed"),
      }
    )
  }
  else {
    broadcastStatusOpts.push(
      {
        value: "SUBMITTED",
        label: intl.msg("status_broadcast_awaiting_review"),
      }
    )
    broadcastStatusOpts.push(
      {
        value: "PUBLISHED\tPUBLISH_FAILED",
        label: intl.msg("status_broadcast_awaiting_stats"),
      }
    )
  }

  broadcastStatusOpts.push({
    value: "STARTED",
    label: intl.msg("status_broadcast_started"),
  })

  broadcastStatusOpts.push({
    value: "STOPPED",
    label: intl.msg("status_broadcast_stopped"),
  })

  broadcastStatusOpts.push({
    value: "ENDED",
    label: intl.msg("status_broadcast_ended"),
  })

  function findBroadcastStatusOpt(status) {
    if (!status) {
      return null
    }
    const st = status.toUpperCase()
    return broadcastStatusOpts.find(i => i.value?.toUpperCase() === st)
  }

  return {
    broadcastStatusOpts,
    findBroadcastStatusOpt
  }
}

export function BroadcastReachInfographics(props) {
  const {intl} = useIntlEx()
  const pctReach = props?.pctReach || 0
  const ttlImpressions = props?.ttlImpressions || 0
  const size = props?.size || 160
  const impX = size / 2
  const impY = size / 2
  return (
    <div>
      <div className="d-flex justify-content-center mt-0 mb-2 gap-1">
        <div style={{color: "var(--form-control-label-color"}}>
          {intl.msg("broadcast_estimated_reach_infographics_estimated_reach")}:
        </div>
        <div style={{color: "var(--form-control-text-color"}}>
          {intl.num(pctReach)}%
        </div>
      </div>
      <div className="d-flex justify-content-center">
        <PieChart3 percentage={pctReach} size={size}>
          <text x={impX} y={impY + 4} textAnchor="middle" dominantBaseline="middle" fontSize="20"
                fill="var(--pie-chart-primary-text-color)">
            {intl.int(ttlImpressions, "compact")}
          </text>
          <text x={impX} y={impY + 24} textAnchor="middle" dominantBaseline="middle" fontSize="12"
                fill="var(--pie-chart-secondary-text-color)">
            Views
          </text>
        </PieChart3>
      </div>
    </div>
  )
}

export function useBroadcastTargetLocationTypeOpts(filter) {
  const {intl} = useIntlEx()
  const broadcastTargetLocationTypeOpts = [
    {
      value: "COORDS",
      text: intl.msg("broadcast_target_location_type_coords"),
      label: intl.msg("broadcast_target_location_type_coords"),
    },
    {
      value: "ADDRESSES",
      text: intl.msg("broadcast_target_location_type_addresses"),
      label: intl.msg("broadcast_target_location_type_addresses"),
    },
  ].sort((a, b) => a.text.localeCompare(b.text));

  function findBroadcastTargetLocationTypeOpt(type) {
    if (!type) {
      return null
    }
    const st = type.toUpperCase()
    return broadcastTargetLocationTypeOpts.find(i => i.value?.toUpperCase() === st)
  }

  return {
    broadcastTargetLocationTypeOpts,
    findBroadcastTargetLocationTypeOpt
  }

}

export function useAdSystemOpts(filter) {
  const {intl} = useIntlEx()
  const adSystemOpts = [
    {
      value: "AMOBEE", label: "Nexxen"
    },
    {
      value: "NEXXEN", label: "Nexxen"
    },
    {
      value: "GOODWAY", label: "Goodway"
    },
    {
      value: "MANUAL", label: "Manual"
    },
  ]

  function findAdSystemOpt(type, def = null) {
    if (!type) {
      return def
    }
    const st = type.toUpperCase()
    return adSystemOpts.find(i => i.value?.toUpperCase() === st) || def
  }

  return {
    adSystemOpts,
    findAdSystemOpt
  }
}

export function BroadcastSummaryWidgets(props) {

  const { api, auth: { session: { user, tenant } } } = useSolv()
  const { intl } = useIntlEx()

  const stats = (props?.broadcast?.adViews || props?.broadcast?.adClicks) ? props?.broadcast : props?.broadcast?.stats
  const adViews = stats?.adViews
  const adClicks = stats?.adClicks
  const cost = stats?.budgetSpent

  log.debug(">>>Z: BroadcastSummaryWidgets: stats=", stats)

  function Metric(props) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center">
        <div className="d-flex flex-row align-items-center p-2 gap-1">
          <span className={styles["primary-metric"]}>
            {props.primaryMetric}
          </span>
          {
            props.secondaryMetric &&
              <span className={styles["secondary-metric"]}>
                {props.secondaryMetric}
              </span>
          }
        </div>
        {
          props.details &&
            <small>{props.details}</small>
        }
      </div>
    )
  }

  function BroadcastCost() {
    return (
      <Widget title={`${intl.msg("spent")}`} size={props.size} align="center" direction="column">
        <WidgetMetric title={`${intl.int(cost)} Credits`}>
          <Metric primaryMetric={cost > 0 ? intl.num(cost, "compact") : <Icon name="elipsis"/>}/>
          {/*<Metric primaryMetric={cost > 0 ? intl.num(cost, "compact") : <Icon name="elipsis"/>} secondaryMetric={`/ ${intl.num(targetBudget, "compact")}`}/>*/}
        </WidgetMetric>
      </Widget>
    )
  }

  // log.debug(">>>Z: BroadcastSummaryWidgets: adViews=", adViews)

  function BroadcastViewStats() {
    return (
      <Widget title={intl.msg("views")} size={props.size} align="center" direction="column">
        <WidgetMetric title={adViews ? intl.int(adViews) : "N/A"}>
          <Metric primaryMetric={adViews > 0 ? intl.int(adViews, "compact") : <Icon name="elipsis"/>}/>
          {/*<Metric primaryMetric={adViews > 0 ? intl.int(adViews, "compact") : <Icon name="elipsis"/>} secondaryMetric={`/ ${intl.int(targetAdViews, "compact")}`}/>*/}
        </WidgetMetric>
      </Widget>
    )
  }

  function BroadcastClickStats() {
    return (
      <Widget title={intl.msg("clicks")} size={props.size} align="center" direction="column">
        <WidgetMetric title={adClicks ? intl.int(adClicks) : "N/A"}>
          <Metric primaryMetric={adClicks > 0 ? intl.int(adClicks, "compact") : <Icon name="elipsis"/>}/>
        </WidgetMetric>
      </Widget>
    )
  }

  function BroadcastCtrStats() {
    let ctr
    if (adClicks) {
      if (adClicks === 0) {
        ctr = 0.0
      }
      else {
        ctr = adClicks / adViews * 100.00
      }
    }
    return (
      <Widget title={intl.msg("ctr")} size={props.size} align="center" direction="column">
        <WidgetMetric title={ctr ? intl.num(ctr) : "N/A"}>
          <Metric primaryMetric={ctr > 0 ? `${intl.num(ctr, "compact")}` : <Icon name="elipsis"/>}/>
        </WidgetMetric>
      </Widget>
    )
  }

  return (
    <div className={`${styles["widget-group"]}`}>
      <BroadcastViewStats/>
      <BroadcastClickStats/>
      <BroadcastCtrStats/>
      {
        user.isSystem() &&
          <BroadcastCost/>
      }
    </div>
  )

}

export function BroadcastView(props) {

  const {intl} = useIntlEx()
  const {findClickActionOpt} = useClickActionOpts()
  const {findBroadcastStartTimeOpt} = useBroadcastStartTimeOpt()
  const {findBroadcastEndTimeOpt} = useBroadcastEndTimeOpt()

  const clickActionOpt = findClickActionOpt(props.broadcast?.pageType)
  const clickDestination = clickActionOpt?.destination(props.broadcast?.pageUrl, props?.tenant?.countryCode)

  if (props.broadcast) {
    return (
      // <FormGroups>

        <BsRow>

          <BsCol cols="auto" breakpoint="md" className="pr-md-0">

            <FormGroup className="w-min-content-md h-fill-height">
              <FormGroup.Label>
                {intl.msg("broadcast_message_custom_image_message_type_photo")}
              </FormGroup.Label>
              <FormGroup.Control>
                <CreativePreview
                  tenantName={props.tenant.displayName}
                  creativeType={props.broadcast?.creativeType}
                  creativeHeader={props.broadcast?.creativeHeader ? props.broadcast?.creativeHeader : ""}
                  creativeTitle={props.broadcast?.creativeTitle}
                  creativeContent={props.broadcast?.creativeContent}
                  creativeFormFields={props.broadcast?.creativeFormFields}
                  creativeFooter={props.broadcast?.creativeFooter}
                  creativeStyle={props.broadcast?.creativeStyle}
                  logoUrl={props.tenant.logoUrl}
                  creativeImageUrl={props.broadcast?.creativeImageUrl}
                  pageUrl={props.broadcast?.pageUrl}
                  borderColor={props.broadcast?.creativeBorderColor}
                  allowedDomains={props.tenant.allowedDomains}/>
              </FormGroup.Control>
            </FormGroup>

          </BsCol>

          <BsCol breakpoint="md">

            <FormGroups>

              <FormGroup>
                <FormGroup.Label compact>
                  {intl.msg("broadcast_click_action")}
                </FormGroup.Label>
                <FormGroup.Controls style={{width: "400px"}}>
                    {
                      clickActionOpt &&
                        <div className="d-flex align-items-center gap-1">
                          <span>
                            {clickActionOpt.icon}
                          </span>
                          <span style={{whiteSpace: "nowrap"}}>
                            {clickActionOpt.text}<span className="text-muted">:</span>
                          </span>
                          <a className="text-link" style={{display: "block", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", width: "100%"}} href={props.broadcast?.pageUrl} title={props.broadcast?.pageUrl} target="_blank">
                            {clickDestination}
                          </a>
                        </div>
                    }
                </FormGroup.Controls>
              </FormGroup>

              <FormGroup>
                <FormGroup.Label compact>
                  {intl.msg("broadcast_target_locations")}
                </FormGroup.Label>
                <FormGroup.Control>
                  {
                    props.broadcast?.targetLocationOpts
                      ?.filter(t => !!t.label)
                      ?.map(t => ("COORDS" === t.type)
                        ? <div>{`Area(${t.label})` }</div>
                        : <div>
                            {t.label}
                            {/*{*/}
                            {/*  props?.tenant?.region?.countryCodes?.length > 1 ? (*/}
                            {/*    <div style={{display: "flex", flexWrap: "nowrap", alignItems: "center", gap: "0.2em"}}>*/}
                            {/*      <Icon name="country_flag" countryCode={t.countryCode} />*/}
                            {/*      {t.label}*/}
                            {/*    </div>*/}
                            {/*  ) : (*/}
                            {/*    t.label*/}
                            {/*  )*/}
                            {/*}*/}
                          </div>)
                  }
                </FormGroup.Control>
              </FormGroup>

              <FormGroup>
                <FormGroup.Label compact>
                  {intl.msg("broadcast_target_budget")}
                </FormGroup.Label>
                <FormGroup.Control cols="9" breakpoint="sm">
                  {intl.num(props.broadcast?.targetBudget)} Credits
                </FormGroup.Control>
              </FormGroup>

              <FormGroup>
                <FormGroup.Label compact>
                  {intl.msg("broadcast_target_schedule")}
                </FormGroup.Label>
                <FormGroup.Control cols="9" breakpoint="sm">
                  <div className="d-flex gap-2 flex-wrap  align-items-center">
                    <div className="d-flex gap-1 align-items-center" >
                      <span style={{color: "var(--label-color)"}}>{intl.msg("broadcast_target_schedule_start_time")}:</span>
                      {findBroadcastStartTimeOpt(props.broadcast?.targetStartTime)?.text}
                    </div>
                    <div className="d-flex gap-1 align-items-center">
                      <span style={{color: "var(--label-color)"}}>{intl.msg("broadcast_target_schedule_end_time")}:</span>
                      {findBroadcastEndTimeOpt(props.broadcast?.targetEndTime)?.text}
                    </div>
                  </div>
                </FormGroup.Control>
              </FormGroup>

              <FormGroup>
                <FormGroup.Label compact>
                  {intl.msg("broadcast_target_views")}
                </FormGroup.Label>
                <FormGroup.Control cols="9" breakpoint="sm">
                  {intl.int(props.reachEstimates?.ttlImpressions || 0)} Views
                </FormGroup.Control>
              </FormGroup>

            </FormGroups>

          </BsCol>

        </BsRow>

      // </FormGroups>

    )
  }
  else {
    return (<></>)
  }

}

export function calcReachEstimates({targetLocationOpts, targetBudget, creativeType, usdMultiplier, tenant}) {

  let ttlPop = 0

  let locations = []
  if (targetLocationOpts) {
    locations = targetLocationOpts.filter(l => l.pop > 0)
  }

  locations.forEach(l => (l && l.pop ? ttlPop = ttlPop + (l.pop > 0 ? l.pop : (l.pop < 0) ? 0 : 1000) : 0))

  const adSystemProps = tenant?.effProps
  // log.debug(">>>Z: calcReachEstimates: adSystemProps=", adSystemProps)
  const cpm = adSystemProps ? (adSystemProps.adCampaignCpms ? adSystemProps.adCampaignCpms[creativeType] : 5.0) : 5.0
  // log.debug(">>>Z: calcReachEstimates: adSystemProps.cpm=", cpm)
  const impressionsPerUsd = Math.trunc(1000 / cpm)
  // log.debug(">>>Z: calcReachEstimates: targetBudget=", targetBudget)
  // log.debug(">>>Z: calcReachEstimates: usdMultiplier=", usdMultiplier )
  const ttlImpressions = Math.trunc(targetBudget * usdMultiplier / cpm * 1000)
  // log.debug(">>>Z: calcReachEstimates: ttlImpressions=", ttlImpressions)

  let pctReach = ceilNum(ttlImpressions / ttlPop * 100)
  if (pctReach === Infinity) {
    pctReach = 0.00
  }

  let budgetToReach100Pct = ceilNum(ttlPop / impressionsPerUsd / usdMultiplier)
  let res = {
    cpm: cpm,
    ttlPop: ttlPop,
    ttlImpressions: ttlImpressions,
    pctReach: pctReach,
    budgetToReach100Pct: budgetToReach100Pct
  }
  // log.debug(">>>Z: calcReachEstimates: res=", res)

  return (res)
}

function washOutColor(hex, amount = 0.5) {
  // Remove #
  hex = hex.replace('#', '');

  // Expand 3-digit hex to 6-digit
  if (hex.length === 3) {
    hex = hex.split('').map(c => c + c).join('');
  }

  // Convert hex to RGB
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  // Blend with white
  const newR = Math.round(r + (255 - r) * amount);
  const newG = Math.round(g + (255 - g) * amount);
  const newB = Math.round(b + (255 - b) * amount);

  return `rgb(${newR}, ${newG}, ${newB})`;
}

export function BroadcastViewInfographics({ potential, target}) {

  const {intl} = useIntlEx()
  const animRef = useRef(null);

  const ratio = ((target && potential) ? target / potential : 0)

  useEffect(() => {
    if (animRef.current) {
      animRef.current.beginElement();
    }
  }, [ratio]);

  return (
    <div style={{width: "100%", height: "auto"}}>

      <div style={{width: "100%"}}>
        <div style={{fontSize: "15px", color: "var(--form-control-label-color)", display: "flex", justifyContent: "center", gap: "0.25em"}}>
          <span>{intl.msg("broadcast_target_views")}:</span><span style={{fontWeight: "700"}}>{intl.int(target)}<sup className="text-muted"> 1</sup></span>
        </div>
      </div>

      <div style={{width: "100%", height: "140px"}}>

        <svg width="100%" height="100%" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">

          <line x1="100" y1="100" x2="20" y2="196" stroke="var(--form-border-color)" strokeWidth="3"/>

          <circle cx="100" cy="100" r="80" fill="var(--form-background-color)" stroke="var(--form-border-color)" strokeWidth="3" />

          <line x1="100" y1="100" x2="20" y2="4" stroke="var(--app-accent-color)" strokeWidth="3"/>

          <circle cx="100" cy="100" r="0" fill={washOutColor(getPropertyComputedValue("--app-accent-color"), 0.37)} stroke="var(--app-accent-color)" strokeWidth="3">
            <animate ref={animRef} attributeName="r" from="0" to={Math.min(ratio, 1.0) * 80} dur="0.5s" fill="freeze"/>
          </circle>

          {/*{*/}
          {/*  (ratio > 0.25) ? (*/}
              <text x="100" y="100" textAnchor="middle" dominantBaseline="middle" fill={getPropertyComputedValue("--primary-button-text-color")} fontSize="14">{intl.num(ceilNum(ratio * 100))}%</text>
          {/*  ) : (*/}
          {/*    <></>*/}
          {/*  )*/}
          {/*}*/}

        </svg>

      </div>

      <div style={{width: "100%"}}>
        <div style={{fontSize: "15px", color: "var(--form-control-label-color)", display: "flex", justifyContent: "center", gap: "0.25em"}}>
          <span>{intl.msg("broadcast_available_views")}:</span><span style={{fontWeight: "700"}}>{intl.int(potential)}<sup className="text-muted"> 2</sup></span>
        </div>
      </div>

    </div>
  )

}

